import { useEffect, useRef } from 'react';

import { defaultRegistry } from 'react-sweet-state';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { findMessageByInvocationId } from '@atlassian/conversation-assistant-action-helpers/utils';
import { getConfluenceResultAcceptanceAttributesForAskQuestion } from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions/get-confluence-result-acceptance-attributes-for-ask-question';
import { getActionContentId } from '@atlassian/conversation-assistant-action-helpers/utils/get-action-data';
import { useChatModeStoreActions } from '@atlassian/conversation-assistant-chat-prompt-input/useChatModeStoreActions';
import {
	encodeAdf,
	encodePromptToAdf,
} from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import {
	createRemixRegenerateCollect,
	type RemixRegenerateSource,
} from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import { useChatContextStoreActions } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import { useChatSignals } from '@atlassian/conversation-assistant-store/controllers/chat-signals';
import {
	isStoreAssistant,
	type StoreAssistant,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useChatMessagesActions } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';
import {
	useMessageIdSelectedForPreview,
	useStagingAreaState,
} from '@atlassian/conversation-assistant-store/controllers/staging-area';
import { useUserPreferences } from '@atlassian/conversation-assistant-store/controllers/user-preferences';
import { isDefaultRovoChat } from '@atlassian/conversation-assistant-utils/default-agent';
import { ROVO_CHAT_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-chat-client';
import { AifcContentCopiedCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/aifc-content-copied-command';
import { AifcExperienceTrackingCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/aifc-preview-experience-tracking-command';
import {
	GetBackgroundConversationActionsCommand,
	ResolveBackgroundConversationActionsCommand,
} from '@atlassian/rovo-content-bridge-api-commands/commands/background-conversation-actions-command';
import {
	ContentDestroyCommand,
	ContentReadyCommand,
} from '@atlassian/rovo-content-bridge-api-commands/commands/content-lifecycle-commands';
import { getGeneratedVisualBlockCounts } from '@atlassian/rovo-content-bridge-api-commands/commands/generated-visual-block-counts';
import { InlineInputBoxAbortMessageCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/inline-input-box-abort-message-command';
import { InlineInputBoxSendMessageCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/inline-input-box-send-message-command';
import {
	SmartCreationModalActivatedCommand,
	SmartCreationModalDeactivatedCommand,
} from '@atlassian/rovo-content-bridge-api-commands/commands/smart-creation-modal-commands';
import { StartBackgroundAgentConversationCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/start-background-agent-conversation-command';
import { isWebviewContext } from '@atlassian/rovo-content-bridge-api/desktop-apis';
import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api/singleton';
import { resolvePreviewAction } from '@atlassian/smart-creation/utils';

import { useGlobalConfig } from '../../../common/utils/global-config/useGlobalConfig';
import { useUIConfig } from '../../../common/utils/ui-config/useUIConfig';
import { useCopyPasteAdopted } from '../../../common/utils/use-copy-paste-adopted/use-copy-paste-adopted';
import { chatStore } from '../../chat-store/chatStore';
import { useChatStore } from '../../chat-store/useChatStore';
import { useChatStoreActions } from '../../chat-store/useChatStoreActions';
import { broadcastStreamStatus } from '../streaming-lifecycle/broadcastStreamStatus';
import { StreamLifecycleState } from '../streaming-lifecycle/StreamLifecycleState';
import { BackgroundConversationActionsCoordinator } from './backgroundConversationActions';
import { startBackgroundConversation } from './startBackgroundConversation';
import { usePromptStream } from './use-prompt-stream';

export const BridgeAPIHandlers = () => {
	const publish = usePublish('ai-mate');
	const [{ status }] = useChatSignals();
	const statusRef = useRef(status);
	statusRef.current = status;
	const [{ currentConversationId, conversations }] = useChatStore();
	const currentConversationIdRef = useRef(currentConversationId);
	currentConversationIdRef.current = currentConversationId;
	const currentConversationRef = useRef(
		currentConversationId ? conversations[currentConversationId] : undefined,
	);
	currentConversationRef.current = currentConversationId
		? conversations[currentConversationId]
		: undefined;
	const {
		sendMessage,
		abortRequests,
		createSeededConversation,
		sendMessageConfirmedTools,
		resolveConversationAction,
		warmConversationCache,
		setConversationData,
	} = useChatStoreActions();
	const { getChatMode } = useChatModeStoreActions();
	const { productKey } = useGlobalConfig();
	const { browserContextSanitizer } = useUIConfig();
	const { preferences } = useUserPreferences();
	const { storeUserMessages } = preferences;
	const confirmedToolsContextRef = useRef({
		getChatMode,
		sendMessageConfirmedTools,
		productKey,
		browserContextSanitizer,
		storeUserMessages,
	});
	confirmedToolsContextRef.current = {
		getChatMode,
		sendMessageConfirmedTools,
		productKey,
		browserContextSanitizer,
		storeUserMessages,
	};
	const sendBackgroundConfirmedTools = (
		params: Parameters<typeof sendMessageConfirmedTools>[0],
	) => {
		const context = confirmedToolsContextRef.current;
		const chatMode = context.getChatMode(params.conversationId ?? '');
		return context.sendMessageConfirmedTools({
			...params,
			storeUserMessages: context.storeUserMessages,
			webSearchEnabled: chatMode.webSearchEnabled,
			deepResearchEnabled: chatMode.deepResearchEnabled,
			thinkDeeperEnabled: chatMode.thinkDeeperEnabled,
			conciergeEnabled: chatMode.conciergeEnabled,
			alphaExtendedThinkingMode: chatMode.alphaExtendedThinkingMode,
			fastModeEnabled: chatMode.fastModeEnabled,
			taskModeEnabled: chatMode.taskModeEnabled,
			productKey: context.productKey,
			addCurrentPageAsContextUserPreference: chatMode.useCurrentPageContext,
			browserContextSanitizer: context.browserContextSanitizer,
		});
	};
	const {
		getTarget,
		getContentContext,
		getCurrentTargetId,
		setTarget,
		clearTarget,
		getActiveContext,
		setCurrentTargetId,
	} = useChatContextStoreActions();
	const promptStreamHook = usePromptStream();
	const experienceTracker = useAIMateExperienceTracker();
	const { trackAIMAUEvent } = useAnalytics();
	const { registerCopy } = useCopyPasteAdopted({ usePersistentPasteListener: true });
	const [messageIdSelectedForPreview] = useMessageIdSelectedForPreview();
	const { stagingAreaEntryPoint, invocationIdSelectedForPreview } = useStagingAreaState();
	const { getMessages } = useChatMessagesActions();
	const backgroundActionsCoordinatorRef = useRef<
		BackgroundConversationActionsCoordinator | undefined
	>(undefined);
	if (!backgroundActionsCoordinatorRef.current) {
		backgroundActionsCoordinatorRef.current = new BackgroundConversationActionsCoordinator(
			getMessages,
			{
				sendMessageConfirmedTools: sendBackgroundConfirmedTools,
				resolveConversationAction,
				warmConversationCache,
				setConversationData,
			},
			// Recovery runs after cache hydration, so read the store at command time rather than render time.
			(conversationId) =>
				defaultRegistry.getStore(chatStore).storeState.getState().conversations[conversationId],
		);
	}
	const isMountedRef = useRef(false);
	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
			backgroundActionsCoordinatorRef.current?.clear();
		};
	}, []);
	const messageIdSelectedForPreviewRef = useRef(messageIdSelectedForPreview);
	messageIdSelectedForPreviewRef.current = messageIdSelectedForPreview;
	const invocationIdSelectedForPreviewRef = useRef(invocationIdSelectedForPreview);
	invocationIdSelectedForPreviewRef.current = invocationIdSelectedForPreview;
	const stagingAreaEntryPointRef = useRef(stagingAreaEntryPoint);
	stagingAreaEntryPointRef.current = stagingAreaEntryPoint;
	const trackAIMAUEventRef = useRef(trackAIMAUEvent);
	trackAIMAUEventRef.current = trackAIMAUEvent;

	const bridgeAPI = getBrowserBridgeAPI();

	const isFirstInlineMessageOnGlobalContent = useRef(false);
	const storeUserMessagesRef = useRef(storeUserMessages);
	storeUserMessagesRef.current = storeUserMessages;

	useEffect(() => {
		// we don't want to clash between the rovo chat client which is offered and implemented in desktop
		// and web; in a future iteration, we will re-use the below code in desktop itself.
		if (isWebviewContext()) {
			return;
		}

		bridgeAPI.on(ROVO_CHAT_CLIENT_ID, ContentReadyCommand, (command) => {
			const ctx = command.contentContext;
			setTarget(ctx, {
				id: command.contentId,
				type: command.contentType,
				canProvideContext: command.canProvideContext,
			});

			const currentContext = getActiveContext();
			isFirstInlineMessageOnGlobalContent.current = currentContext === 'global';

			// Mark this content as the active target for staging-area context.
			// This ensures broadcasts and follow-up messages use the correct content
			// even if the modal re-sets the page target.
			if (
				(ctx === 'staging-area' || currentContext === 'staging-area') &&
				expValEquals('cc-maui-experiment', 'isEnabled', true)
			) {
				setCurrentTargetId(command.contentId);
			}
		});

		bridgeAPI.on(ROVO_CHAT_CLIENT_ID, ContentDestroyCommand, (command) => {
			const ctx = command.contentContext;

			// Targets are stored as arrays; clear by specific ID for the target content.
			clearTarget(ctx, command.contentId);

			const currentContext = getActiveContext();
			isFirstInlineMessageOnGlobalContent.current = currentContext === 'global';
		});

		bridgeAPI.on(ROVO_CHAT_CLIENT_ID, StartBackgroundAgentConversationCommand, (command) => {
			if (
				!isExperimentEnabled('platform_editor_inline_agentic_comments') ||
				statusRef.current !== 'online' ||
				!command.prompt ||
				!command.agentId
			) {
				return false;
			}
			void startBackgroundConversation(
				command,
				{ createSeededConversation, sendMessage },
				storeUserMessagesRef.current,
				publish,
				(session) => {
					if (isMountedRef.current) {
						backgroundActionsCoordinatorRef.current?.register(session);
					}
				},
			);
			return true;
		});

		bridgeAPI.on(ROVO_CHAT_CLIENT_ID, GetBackgroundConversationActionsCommand, (command) => {
			if (!isExperimentEnabled('platform_editor_inline_agentic_comments')) return undefined;
			return backgroundActionsCoordinatorRef.current?.get(
				command.sourceId,
				command.conversationId,
				command.commentId,
			);
		});

		bridgeAPI.on(ROVO_CHAT_CLIENT_ID, ResolveBackgroundConversationActionsCommand, (command) => {
			if (!isExperimentEnabled('platform_editor_inline_agentic_comments'))
				return { status: 'stale' };
			return backgroundActionsCoordinatorRef.current?.resolve(command) ?? { status: 'stale' };
		});

		bridgeAPI.on(ROVO_CHAT_CLIENT_ID, InlineInputBoxSendMessageCommand, (command) => {
			if (!command.prompt || !command.contentId) {
				return;
			}

			setCurrentTargetId(command.contentId);

			const encodedPrompt = command.skillMetadata
				? encodePromptToAdf(command.prompt, command.skillMetadata).adf
				: encodeAdf(command.prompt);
			const reusesMatchingDefaultRovoConversation =
				command.reuseMatchingConversation &&
				isDefaultRovoChat({
					agentId: currentConversationRef.current?.agent?.agent_id,
					agentNamedId: currentConversationRef.current?.agent?.agent_named_id,
				});

			sendMessage({
				...command,
				prompt: encodedPrompt,
				// TODO(ai_mate_store_messages_be_driven): remove this local storeUserMessages payload
				// value when BE user settings are fully rolled out; BE overrides it via the capability header.
				storeUserMessages: storeUserMessagesRef.current,
				promptStreamHook,
				messageSources: {
					interactionSource:
						command.source === 'CONFLUENCE_MAUI_INLINE_EDITOR'
							? 'inlineRovoFromRemix'
							: 'inlineRovo',
					followUpSource:
						command.source === 'CONFLUENCE_MAUI_INLINE_EDITOR'
							? 'inlineRovoFromRemix'
							: 'inlineRovo',
					// Mark the Create with Rovo blank-object route on AIMAU events.
					entryPoint:
						command.source === 'CONFLUENCE_CWR_BLANK_OBJECT' ? 'cwrBlankObject' : undefined,
				},
				// Using an explicitly selected agent with inline prompt should always start a new chat,
				// unless a scoped default-Rovo request already has a matching Rovo conversation.
				// The first inline prompt message on a new content ID should always start a new chat.
				conversationId:
					(command.agentId && !reusesMatchingDefaultRovoConversation) ||
					command.shouldStartNewConversation ||
					isFirstInlineMessageOnGlobalContent.current
						? EMPTY_CONVERSATION_ID
						: undefined,
			});

			isFirstInlineMessageOnGlobalContent.current = false;
		});

		bridgeAPI.on(ROVO_CHAT_CLIENT_ID, InlineInputBoxAbortMessageCommand, (command) => {
			abortRequests({
				conversationId: command.conversationId ?? currentConversationIdRef.current,
			});
			if (expValEquals('platform_editor_remove_pause_streaming', 'isEnabled', true)) {
				const currentTargetId = getCurrentTargetId();
				const currentTargetContext = currentTargetId
					? (getContentContext(currentTargetId) ?? getActiveContext())
					: getActiveContext();
				const activeTarget = getTarget(currentTargetContext, currentTargetId);
				const activeTargetType = activeTarget?.type;
				if (activeTarget?.id && activeTargetType === 'page') {
					broadcastStreamStatus(StreamLifecycleState.ABORTED, `inline-abort-${Date.now()}`, {
						id: activeTarget.id,
						type: activeTargetType,
					});
				}
			}
			experienceTracker.abort({
				name: ExperienceName.REMIX_REGENERATE,
				reason: 'User cancelled remix thinking',
			});
		});

		bridgeAPI.on(ROVO_CHAT_CLIENT_ID, AifcExperienceTrackingCommand, (command) => {
			if (!command?.status || !command.experienceName) {
				return;
			}

			const featureArea = command.attributes?.featureArea as string | undefined;

			if (command.status === 'start') {
				const attributes = {
					...(command.attributes || {}),
					...(featureArea !== undefined && { featureArea }),
					...(command.actionType && { actionType: command.actionType }),
					...(command.actionId && { actionId: command.actionId }),
					...(command.contentContext && { contentContext: command.contentContext }),
					...(command.dynamicUiSubtype && { dynamicUiSubtype: command.dynamicUiSubtype }),
					...(command.remixType && { remixType: command.remixType }),
					...(command.remixSubtype && { remixSubtype: command.remixSubtype }),
					...(currentConversationIdRef.current && {
						conversationChannelId: currentConversationIdRef.current,
					}),
				};
				let collect;
				// Collect callbacks have to be constructed parent-side because functions are
				// not serializable and cannot be sent over the bridge as command payload data.
				switch (command.experienceName) {
					case ExperienceName.REMIX_REGENERATE: {
						collect = createRemixRegenerateCollect({
							conversationChannelId: currentConversationIdRef.current,
							regenerateSource: command.attributes?.regenerateSource as
								| RemixRegenerateSource
								| undefined,
							featureArea,
							allowCollectFallback: true,
							targetIds: {
								actionId: command.actionId ?? undefined,
								selectionLocalId: command.actionId ?? undefined,
							},
						});
						break;
					}
					default:
						break;
				}
				experienceTracker.start({
					name: command.experienceName,
					id: command.messageId ?? command.actionId ?? command.experienceName,
					attributes,
					...(collect ? { collect } : {}),
				});
			} else if (command.status === 'success') {
				experienceTracker.succeed({
					name: command.experienceName,
					...(command.messageId && { id: command.messageId }),
					attributes: {
						...(command.attributes || {}),
						...(featureArea !== undefined && { featureArea }),
						...(command.actionType && { actionType: command.actionType }),
						...(command.actionId && { actionId: command.actionId }),
						...(command.contentContext && { contentContext: command.contentContext }),
						...(command.dynamicUiSubtype && { dynamicUiSubtype: command.dynamicUiSubtype }),
						...(command.remixType && { remixType: command.remixType }),
						...(command.remixSubtype && { remixSubtype: command.remixSubtype }),
						...(command.isSafetyViolation && { isSafetyViolation: command.isSafetyViolation }),
					},
				});
			} else if (command.status === 'fail') {
				experienceTracker.fail({
					name: command.experienceName,
					...(command.messageId && { id: command.messageId }),
					error: new Error(command.error ?? 'Unknown error'),
					attributes: {
						...(command.attributes || {}),
						...(featureArea !== undefined && { featureArea }),
						...(command.actionType && { actionType: command.actionType }),
						...(command.actionId && { actionId: command.actionId }),
						...(command.contentContext && { contentContext: command.contentContext }),
						...(command.dynamicUiSubtype && { dynamicUiSubtype: command.dynamicUiSubtype }),
						...(command.remixType && { remixType: command.remixType }),
						...(command.remixSubtype && { remixSubtype: command.remixSubtype }),
					},
				});
			} else if (command.status === 'abort') {
				const isKillswitchEnabled = FeatureGates.getExperimentValue(
					'platform_aifc_bridge_experience_tracker_fix_aborts',
					'value',
					false,
				);
				const attributes = {
					...(command.attributes || {}),
					...(featureArea !== undefined && { featureArea }),
					...(command.actionType && { actionType: command.actionType }),
					...(command.actionId && { actionId: command.actionId }),
					...(command.contentContext && { contentContext: command.contentContext }),
					...(command.dynamicUiSubtype && { dynamicUiSubtype: command.dynamicUiSubtype }),
				};
				if (isKillswitchEnabled) {
					experienceTracker.fail({
						name: command.experienceName,
						...(command.messageId && { id: command.messageId }),
						error: new Error(command.reason ?? 'Aborted'),
						attributes,
					});
				} else {
					experienceTracker.abort({
						name: command.experienceName,
						...(command.messageId && { id: command.messageId }),
						reason: command.reason ?? 'Aborted',
						attributes,
					});
				}
			}
		});

		bridgeAPI.on(ROVO_CHAT_CLIENT_ID, SmartCreationModalActivatedCommand, (command) => {
			setTarget('staging-area', {
				id: command.type, // Currently, there is only Jira is using this Command and Jira doesn't have a content id for the smart creation modal
				type: command.type,
				canProvideContext: true,
			});
		});

		bridgeAPI.on(ROVO_CHAT_CLIENT_ID, SmartCreationModalDeactivatedCommand, (command) => {
			if (getTarget('staging-area')?.id === command.type) {
				clearTarget('staging-area', command.type);

				if (expValEquals('cc-maui-experiment', 'isEnabled', true)) {
					setCurrentTargetId(undefined);
				}
			}
		});

		bridgeAPI.on(ROVO_CHAT_CLIENT_ID, AifcContentCopiedCommand, (command) => {
			const messages = getMessages(currentConversationIdRef.current ?? '');
			const isPreviewActionCorrelationAnalyticsEnabled = fg(
				'aifc_preview_action_correlation_analytics',
			);
			// Match preview resolution: an invocation selection takes priority over a message selection.
			const selectedMessage = invocationIdSelectedForPreviewRef.current
				? findMessageByInvocationId(messages, invocationIdSelectedForPreviewRef.current)
				: messages.find(
						(message): message is StoreAssistant =>
							isStoreAssistant(message) &&
							message.content.message.message.id === messageIdSelectedForPreviewRef.current,
					);
			const selectedActions = selectedMessage?.content.message.message.actions ?? [];
			const selectedAction = invocationIdSelectedForPreviewRef.current
				? resolvePreviewAction(selectedMessage, invocationIdSelectedForPreviewRef.current)
				: selectedActions.find((action) => getActionContentId(action) === command.contentId);
			const analyticsMessageId = isPreviewActionCorrelationAnalyticsEnabled
				? (selectedMessage?.content.message.message.id ??
					messageIdSelectedForPreviewRef.current ??
					undefined)
				: (messageIdSelectedForPreviewRef.current ?? undefined);
			const selectedActionKey = selectedAction?.key;
			const shouldIncludeCwrAnalyticsUpdates = fg('cwr_ai_result_analytics_updates');
			const selectedActionForCorrelation =
				isPreviewActionCorrelationAnalyticsEnabled && invocationIdSelectedForPreviewRef.current
					? selectedMessage?.content.message.message.actions?.find(
							(action) => action.invocationId === invocationIdSelectedForPreviewRef.current,
						)
					: undefined;

			let requestId: string | undefined;
			let traceId: string | undefined;
			if (fg('aifc_request_id_analytics') || fg('aifc_trace_id_analytics')) {
				if (selectedMessage) {
					traceId = selectedMessage.traceId;
					requestId =
						selectedMessage.content.metadata?.request_id ??
						selectedMessage.content.message.message.message_metadata?.requestId;
				}
			}

			const askQuestionAttributes = getConfluenceResultAcceptanceAttributesForAskQuestion({
				messages: getMessages(currentConversationIdRef.current ?? ''),
				contentId: command.contentId,
			});

			trackAIMAUEventRef.current({
				type: 'actioned',
				actionResult: 'copy',
				source: 'AIMate',
				invokedFrom: 'AIFC',
				proactive: false,
				userGenerated: true,
				singleInstrumentationID: analyticsMessageId,
				...(shouldIncludeCwrAnalyticsUpdates && selectedActionKey
					? { messageActions: [selectedActionKey] }
					: {}),
				attributes: {
					...(fg('aifc_request_id_analytics') && requestId ? { request_id: requestId } : {}),
					...(fg('aifc_trace_id_analytics') && traceId ? { trace_id: traceId } : {}),
					...(selectedActionForCorrelation?.key
						? { actionKey: selectedActionForCorrelation.key }
						: {}),
					isInSmartCreateFlow: true,
					createdContentProduct: 'confluence',
					createdContentType: command.contentType,
					copiedCharCount: command.copiedCharCount,
					copiedWordCount: command.copiedWordCount,
					totalDocCharCount: command.totalDocCharCount,
					copiedPercentage: command.copiedPercentage,
					entryPoint: stagingAreaEntryPointRef.current || undefined,
					...getGeneratedVisualBlockCounts(selectedAction?.data),
					...(shouldIncludeCwrAnalyticsUpdates && selectedActionKey
						? {
								actionKey: selectedActionKey,
								userConfirmedActions: [
									{ actionKey: selectedActionKey, objectId: command.contentId },
								],
							}
						: {}),
					...(askQuestionAttributes ?? {}),
				},
			});

			if (shouldIncludeCwrAnalyticsUpdates && selectedActionKey) {
				registerCopy({
					messageId: analyticsMessageId,
					analyticsMessageActions: [selectedActionKey],
					trackAIMAUEvent: trackAIMAUEventRef.current,
					deferClipboardReadToNextTask: true,
				});
			}
		});

		// handlers have been setup - now we can mark ourselves as 'ready' to handle incoming messages
		bridgeAPI.ready(ROVO_CHAT_CLIENT_ID);
		return () => {
			// Unregister the background-conversation handlers on effect restart or unmount.
			bridgeAPI.off(ROVO_CHAT_CLIENT_ID, StartBackgroundAgentConversationCommand);
			bridgeAPI.off(ROVO_CHAT_CLIENT_ID, GetBackgroundConversationActionsCommand);
			bridgeAPI.off(ROVO_CHAT_CLIENT_ID, ResolveBackgroundConversationActionsCommand);
		};
	}, [
		bridgeAPI,
		publish,
		createSeededConversation,
		sendMessage,
		abortRequests,
		promptStreamHook,
		setTarget,
		clearTarget,
		getTarget,
		getContentContext,
		getCurrentTargetId,
		getActiveContext,
		setCurrentTargetId,
		experienceTracker,
		currentConversationId,
		getMessages,
		registerCopy,
	]);

	return null;
};
